import React from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { TableHead } from './ui/table';

/**
 * Sortable column header. Click cycles asc → desc → off.
 *
 * Usage:
 *   <SortableHeader field="name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>
 *     Name
 *   </SortableHeader>
 */
export const SortableHeader = ({
  field,
  sortBy,
  sortOrder,
  onSort,
  children,
  className = '',
  testId,
}) => {
  const active = sortBy === field;
  const Icon = !active
    ? ArrowUpDown
    : sortOrder === 'asc'
      ? ArrowUp
      : ArrowDown;
  const iconColor = active ? 'text-[#2C9DE1]' : 'text-slate-400';

  return (
    <TableHead
      className={`font-semibold cursor-pointer select-none hover:bg-slate-100 ${className}`}
      onClick={() => onSort(field)}
      data-testid={testId || `sort-${field}`}
    >
      {children}
      <Icon className={`inline w-3.5 h-3.5 ml-1 ${iconColor}`} />
    </TableHead>
  );
};

/**
 * Tiny hook that owns sort state and a click handler that cycles asc → desc → off.
 *
 *   const { sortBy, sortOrder, handleSort } = useSortable(onChange);
 *
 * onChange(nextBy, nextOrder) is called whenever the user clicks a header.
 */
export const useSortable = (onChange) => {
  const [sortBy, setSortBy] = React.useState(null);
  const [sortOrder, setSortOrder] = React.useState('asc');

  const handleSort = (field) => {
    let nextBy = field;
    let nextOrder = 'asc';
    if (sortBy === field) {
      if (sortOrder === 'asc') {
        nextOrder = 'desc';
      } else {
        nextBy = null;
        nextOrder = 'asc';
      }
    }
    setSortBy(nextBy);
    setSortOrder(nextOrder);
    onChange?.(nextBy, nextOrder);
  };

  return { sortBy, sortOrder, handleSort, setSortBy, setSortOrder };
};
